import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  transcriptorApi,
  type ConsultationStatus,
  type Document,
  type DocumentSectionUpdateRequest,
} from '@/shared/api/transcriptor';
import { toastErrors } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Badge, Button, toastCaller } from '@/shared/ui';

import type {
  DocumentTemplateSummary,
  EncounterSchema,
} from '@/entities/templates';
import {
  getProcessingStage,
  getTranscriptorErrorMessage,
  transcriptorModel,
  useEncounterDocumentsPolling,
  type TranscriptorConsultation,
} from '@/entities/transcriptor';

import { isDictation, useLiveSession } from '@/features/audioRecording';

import { ENCOUNTER_STATUS_TONES, i18n, type EncounterHeading } from '../config';
import {
  getEncounterTitle,
  useCanEditTemplates,
  useEncounterPresentation,
} from '../hooks';
import {
  formatRecordedLength,
  formatTimecode,
  getRecordedSeconds,
  isFieldListDocument,
  type UnresolvedNoteLine,
} from '../lib';

import { OpenTemplateButton } from './EditTemplateButton';
import { EncounterDocuments } from './EncounterDocuments';
import styles from './EncounterDocumentView.module.scss';
import { EncounterProtocolDocument } from './EncounterProtocolDocument';
import { MissingRequiredSections } from './MissingRequiredSections';
import type { NoteAssumptionsReport } from './NoteAssumptions';
import { ProcessingStages } from './ProcessingStages';
import { TranscriptChunks } from './TranscriptView';

type Props = {
  patientId: string;
  encounterId: string;
  encounter: TranscriptorConsultation | undefined;
  schemas: EncounterSchema[];
  documentTemplates: DocumentTemplateSummary[];
  canEdit: boolean;
  canRecord: boolean;
  className?: string;
};

type SaveToRecordState = 'saving' | 'saved' | 'failed';

const isSavable = (document: Document) =>
  !!document.id &&
  (document.status === 'completed' || document.status === 'edited');

// One encounter's documents in its pop-up: the note as it fills while
// recording, the processing after Stop, and the finished documents with
// "Save to record".
export const EncounterDocumentView: FC<Props> = ({
  patientId,
  encounterId,
  encounter,
  schemas,
  documentTemplates,
  canEdit,
  canRecord,
  className,
}) => {
  const dispatch = useAppDispatch();
  const { formatDate, formatMessage } = useIntl();
  const [documentKey, setDocumentKey] = useState('');
  const [dirty, setDirty] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [saveState, setSaveState] = useState<SaveToRecordState>();
  // Required sections without an answer, block-note lines still to fill and
  // values AI assumed, by document, as the documents last showed them.
  const [missingRequired, setMissingRequired] = useState<
    Record<string, string[]>
  >({});
  const [unresolvedNotes, setUnresolvedNotes] = useState<
    Record<string, UnresolvedNoteLine[]>
  >({});
  const [noteAssumptions, setNoteAssumptions] = useState<
    Record<string, NoteAssumptionsReport>
  >({});
  const session = useLiveSession();
  const recording = useAppSelector((state) => state.transcriptor.recording);
  const canEditTemplates = useCanEditTemplates();
  const { kindOf, kindLabel, statusLabel } = useEncounterPresentation({
    schemas,
    documentTemplates,
  });

  const refresh = () =>
    dispatch(
      transcriptorModel.thunks.fetchConsultationById({
        consultationId: encounterId,
        patientId,
      }),
    ).unwrap();

  useEffect(() => {
    void dispatch(
      transcriptorModel.thunks.fetchConsultationById({
        consultationId: encounterId,
        patientId,
      }),
    );
  }, [encounterId, patientId]);

  useEncounterDocumentsPolling({ patientId, consultation: encounter });

  if (!encounter) {
    return (
      <div className={cn(styles.page, className)}>
        <section className={styles.card}>
          <p className={cn('p3', styles.message)}>
            <FormattedMessage id="global.loading" defaultMessage="Loading…" />
          </p>
        </section>
      </div>
    );
  }

  const live = session?.meta.encounterId === encounter.id ? session.live : null;
  // A dictation has no file to recognise: while scribe finishes it, its note
  // says so itself, without the processing stages.
  const dictated = encounter.recordingMode === 'dictation' || isDictation(live);
  const recordingNow =
    recording.consultationId === encounter.id &&
    (recording.isRecording || recording.isPaused);
  const settled =
    encounter.status === 'succeeded' || encounter.status === 'failed';
  const headerStatus: ConsultationStatus = recordingNow
    ? 'recording'
    : encounter.status;
  const encounterTitle = getEncounterTitle(encounter);
  // A single document sits right under the encounter's header, which already
  // names it and says its status.
  const encounterHeading: EncounterHeading | undefined =
    encounter.documents.length === 1
      ? { title: encounterTitle, status: headerStatus }
      : undefined;
  const savableDocuments = encounter.documents.filter(isSavable);
  // An answer edit after saving withdraws the approval on the server, so the
  // stored flags decide, not the last save here.
  const savedToRecord =
    savableDocuments.length > 0 &&
    savableDocuments.every((document) => document.approved);
  const canSaveToRecord =
    canEdit && encounter.status === 'succeeded' && savableDocuments.length > 0;
  const pendingDocuments = encounter.documents.filter(
    (document) => !document.approved,
  );
  const missing =
    canSaveToRecord && !savedToRecord
      ? [
          ...new Set(
            pendingDocuments.flatMap(
              (document) => missingRequired[document.id ?? ''] ?? [],
            ),
          ),
        ]
      : [];
  // A note's own panels list what holds the save; here it only waits.
  const noteHoldsSave =
    canSaveToRecord &&
    !savedToRecord &&
    pendingDocuments.some(
      (document) =>
        !!unresolvedNotes[document.id ?? '']?.length ||
        !!noteAssumptions[document.id ?? '']?.assumptions.length,
    );
  const transcript =
    recordingNow && live && !encounter.transcriptChunks.length
      ? live.chunks
      : encounter.transcriptChunks;
  const hasTranscript =
    transcript.length > 0 || (recordingNow && !!live?.partial);
  // A note dictated live is finished from its live transcript while the file
  // is recognised later, so its stage is not recognition.
  const stageTranscript = encounter.transcriptChunks.length
    ? encounter.transcriptChunks
    : live?.chunks ?? [];

  const saveDocument = async (
    document: Document,
    sections: DocumentSectionUpdateRequest[],
  ) => {
    if (!document.id) {
      throw new Error('Missing document id');
    }
    await transcriptorApi.updateConsultationDocument(
      encounter.id,
      document.id,
      { sections },
    );
    await refresh();
  };

  const generateDocument = async (templateSeriesId: string) => {
    try {
      await dispatch(
        transcriptorModel.thunks.orderConsultationDocuments({
          consultationId: encounter.id,
          templateSeriesIds: [templateSeriesId],
        }),
      ).unwrap();
    } catch (generationError) {
      const refusal = getTranscriptorErrorMessage(generationError);
      if (refusal) {
        toastCaller({
          type: 'error',
          heading: formatMessage(toastErrors.errorHeading),
          message: formatMessage(refusal),
        });
      }
      throw generationError;
    }
  };

  const saveToRecord = async () => {
    setSaveState('saving');
    const failed = await Promise.all(
      pendingDocuments
        .filter(isSavable)
        .map((document) =>
          transcriptorApi.updateConsultationDocument(
            encounter.id,
            document.id ?? '',
            { approved: true },
          ),
        ),
    )
      .then(refresh)
      .then(
        () => false,
        () => true,
      );
    setSaveState(failed ? 'failed' : 'saved');
  };

  const recordedLength = formatRecordedLength(getRecordedSeconds(encounter));
  const subtitle = [
    formatDate(encounter.date, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    kindLabel(kindOf(encounter)),
    encounter.schemaName,
    recordingNow
      ? formatMessage(
          {
            id: 'workspace.recordingNow',
            defaultMessage: 'recording now {time}',
          },
          { time: formatTimecode(recording.recordingTime) },
        )
      : recordedLength &&
        formatMessage(
          {
            id: 'workspace.recordingLength',
            defaultMessage: 'recording {time}',
          },
          { time: recordedLength },
        ),
  ]
    .filter(Boolean)
    .join(' · ');

  const footer = recordingNow ? (
    isDictation(live) ? (
      <FormattedMessage
        id="workspace.footer.liveRecording"
        defaultMessage="AI fills the documents as you record — edit right away, no need to stop."
      />
    ) : (
      <FormattedMessage
        id="workspace.footer.processing"
        defaultMessage="Documents are generated after the recording stops."
      />
    )
  ) : savedToRecord ? (
    <FormattedMessage
      id="workspace.footer.saved"
      defaultMessage="Saved to the patient record."
    />
  ) : noteHoldsSave ? (
    <FormattedMessage
      id="workspace.footer.saveWaits"
      defaultMessage="Saving to the record waits for the note: the lines and AI values to finish are listed above it."
    />
  ) : canSaveToRecord ? (
    <FormattedMessage
      id="workspace.footer.saveToRecord"
      defaultMessage="Check the documents and save them to the patient record."
    />
  ) : (
    <FormattedMessage
      id="workspace.footer.review"
      defaultMessage="Review the documents before using them in the patient record."
    />
  );

  return (
    <div
      className={cn(styles.page, className)}
      data-section="transcriptor-document"
    >
      <div
        className={cn(
          styles.body,
          hasTranscript && transcriptOpen && styles.withTranscript,
        )}
      >
        <section
          className={cn(styles.card, styles.note)}
          data-component="workspace-encounter"
        >
          <header className={styles.header}>
            <div className={styles.title}>
              <h2 className="h5">{encounterTitle}</h2>
              <p className="p4">{subtitle}</p>
            </div>
            <Badge size="small" tone={ENCOUNTER_STATUS_TONES[headerStatus]}>
              {recordingNow
                ? formatMessage(i18n.encounterStatusMessages.recording)
                : statusLabel(encounter)}
            </Badge>
          </header>
          {!recordingNow && !settled && !dictated && (
            <ProcessingStages
              className={styles.stages}
              stage={getProcessingStage({
                ...encounter,
                transcriptChunks: stageTranscript,
              })}
            />
          )}
          <EncounterDocuments
            encounter={encounter}
            schema={schemas.find((schema) => schema.id === encounter.schemaId)}
            documentTemplates={documentTemplates}
            activeKey={documentKey}
            canEdit={canEdit}
            locked={dirty}
            onActiveKeyChange={setDocumentKey}
            onSave={saveDocument}
            onDirtyChange={setDirty}
            renderDocument={(document, editor) => (
              <EncounterProtocolDocument
                key={document.id}
                encounter={encounter}
                document={document}
                canEdit={canEdit}
                fallback={editor}
                onMissingRequiredChange={(titles) =>
                  setMissingRequired((current) => ({
                    ...current,
                    [document.id ?? '']: titles,
                  }))
                }
                onUnresolvedNoteLinesChange={(lines) =>
                  setUnresolvedNotes((current) => ({
                    ...current,
                    [document.id ?? '']: lines,
                  }))
                }
                onNoteAssumptionsChange={(report) =>
                  setNoteAssumptions((current) => ({
                    ...current,
                    [document.id ?? '']: report,
                  }))
                }
                encounterHeading={encounterHeading}
              />
            )}
            renderActions={(document) =>
              canEditTemplates &&
              isFieldListDocument(document) &&
              document.template_series_id && (
                <OpenTemplateButton
                  seriesId={document.template_series_id}
                  disabled={dirty}
                />
              )
            }
            onGenerateDocument={
              canRecord && !dictated ? generateDocument : undefined
            }
            encounterStatus={encounterHeading?.status}
          />
          <footer className={styles.footer}>
            <p className="p4">{footer}</p>
            {saveState === 'failed' && (
              <p role="alert" className={cn('p4', styles.alert)}>
                <FormattedMessage
                  id="workspace.saveToRecordFailed"
                  defaultMessage="The documents were not saved to the record. Try again."
                />
              </p>
            )}
            {missing.length > 0 && (
              <MissingRequiredSections
                className={styles.missingRequired}
                sections={missing.map((title) => ({ code: title, title }))}
              />
            )}
            {canSaveToRecord && !savedToRecord && (
              <Button
                size="medium"
                disabled={dirty || saveState === 'saving' || noteHoldsSave}
                loading={saveState === 'saving'}
                data-component="workspace-save-to-record"
                onClick={() => void saveToRecord()}
              >
                <FormattedMessage
                  id="workspace.saveToRecord"
                  defaultMessage="Save to record"
                />
              </Button>
            )}
          </footer>
        </section>
        {hasTranscript && (
          <aside
            className={cn(styles.card, styles.transcript)}
            data-component="workspace-transcript"
          >
            <Button
              variant="tertiary"
              size="small"
              icon={transcriptOpen ? 'arrowUp' : 'arrowDown'}
              className={styles.transcriptToggle}
              aria-expanded={transcriptOpen}
              onClick={() => setTranscriptOpen((open) => !open)}
            >
              <FormattedMessage
                id="workspace.document.transcript"
                defaultMessage="Transcript"
              />
            </Button>
            {transcriptOpen && (
              <TranscriptChunks
                className={styles.transcriptChunks}
                chunks={transcript}
                partial={recordingNow ? live?.partial : undefined}
              />
            )}
          </aside>
        )}
      </div>
    </div>
  );
};
